import type { MessageInfo } from '@/types/common';
import type {
	ContentRequest,
	EventCreateRequest,
	EventUpdateRequest,
	EventDetailResponse,
	EventDetail,
	EventCancellationDetailResponse,
	EventDuplicateFormRequest,
	EventSeriesDuplicateResponse,
	EventSeriesStatusResponse,
	EventSeriesPollingRequest,
} from '@/types/event';
import dayjs from 'dayjs';

interface EventContent {
	title: string
	description: string
}
interface EventStore {
	create: {
		data: EventContent | null
		errorMessage: string
		eventId: number | null
	}
	edit: {
		title: string
		data: EventDetailResponse | null
		success: Boolean
		isUpdatedCoverImage: Boolean
		errorMessage: string
		isPublishing: boolean
		surveyFormId: number | null
	}
	delete: {
		errorMessage: string
	}
	cancel: {
		data: EventCancellationDetailResponse | null
		errorMessage: string
		isLoading: boolean
	}
	duplicate: {
		eventId: number | null
		eventSeriesInfo: EventSeriesDuplicateResponse | null
		eventSeriesResult: EventSeriesStatusResponse | null
		errorMessage: string
		isLoading: boolean
	}
	message: {
		errorMessage: string
	},
	aiCoverImage: {
		url: string,
		errorMessage: string
		isLoading: boolean
	},
	isLoading: boolean
}

const useEventStore = defineStore('event', {
	state: (): EventStore => ({
		create: {
			data: {
				title: '',
				description: '',
			},
			errorMessage: '',
			eventId: null,
		},
		edit: {
			title: '',
			data: null,
			success: false,
			isUpdatedCoverImage: false,
			errorMessage: '',
			isPublishing: false,
			surveyFormId: null,
		},
		delete: {
			errorMessage: '',
		},
		duplicate: {
			eventId: null,
			eventSeriesInfo: null,
			eventSeriesResult: null,
			errorMessage: '',
			isLoading: false,
		},
		cancel: {
			data: null,
			isLoading: false,
			errorMessage: '',
		},
		message: {
			errorMessage: '',
		},
		aiCoverImage: {
			url: '',
			isLoading: false,
			errorMessage: '',
		},
		isLoading: false,
	}),

	getters: {
		eventDetail: (state): EventDetail | null => {
			if (!state.edit.data) {
				return null;
			}
			return transformEventDetail(state.edit.data);
		},

		originalEventStatus: (state): string => {
			if (!state.edit.data) {
				return '';
			}

			return state.edit.data.status;
		},

		eventStatus: (state): string => {
			if (!state.edit.data) {
				return '';
			}

			// Derived final status on sold_out event
			if (state.edit.data.status !== EVENT_STATUS.SOLD_OUT) {
				return state.edit.data.status;
			}
			const currentDateTime = getCurrentUtcDateTimeInSpecificTimezone(state.edit.data.end.timezone);
			const isPublish = dayjs(currentDateTime).isBefore(state.edit.data.end.utc);
			const isSoldOutBeforeFinished = (state.edit.data.status === EVENT_STATUS.SOLD_OUT) && isPublish;

			if (isSoldOutBeforeFinished) {
				return EVENT_STATUS.OPEN;
			}
			return EVENT_STATUS.FINISHED;
		},

		isHappeningNow: (state): boolean => {
			if (!state.edit.data || !state.edit.data.start || !state.edit.data.end) {
				return false;
			}
			const isHappeningNow = calculateHappeningNow(state.edit.data.start, state.edit.data.end);

			return isHappeningNow;
		},

		isSoldOutBeforeFinished: (state): boolean | null => {
			if (!state.edit.data || !state.edit.data.end) {
				return null;
			}
			const currentDateTime = getCurrentUtcDateTimeInSpecificTimezone(state.edit.data.end.timezone);
			const isBeforeEndDate = dayjs(currentDateTime).isBefore(state.edit.data.end.utc);

			return (state.edit.data.status === EVENT_STATUS.SOLD_OUT) && isBeforeEndDate;
		},

		isEditableAfterEventEnd: (state): boolean => {
			if (!state.edit.data || !state.edit.data.end) {
				return false;
			}

			// Count 24 hour after event ended
			const currentDateTime = getCurrentUtcDateTimeInSpecificTimezone (state.edit.data.end.timezone);
			const endDate = state.edit.data.end.utc;
			const endDateAdded24Hour = dayjs(state.edit.data.end.utc, DATE_TIME_ISO8601_FORMAT, true).add(24, 'hour').format(DATE_TIME_ISO8601_FORMAT);
			const isWithin24Hour = dayjs(currentDateTime).isBetween(endDate, endDateAdded24Hour);
			return state.edit.data.status === EVENT_STATUS.SOLD_OUT && isWithin24Hour;
		},

		eventSeriesId: (state): string => {
			return state.duplicate.eventSeriesInfo?.eventSeriesId ?? '';
		},

		isUSEvent: (state): boolean => {
			return state.edit.data?.countryId === DEFAULT_COUNTRY;
		},
	},

	actions: {
		async generateContentWithAI(payload: ContentRequest) {
			this.isLoading = true;

			const { data, errorMessage } = await postContentGeneration({
				...payload,
			});

			if (errorMessage.value) {
				this.create.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.create.data = {
					title: data.value.title,
					description: data.value.desc,
				};
			}
			this.isLoading = false;
		},

		async createEvent(payload: EventCreateRequest) {
			this.isLoading = true;

			const { data, errorMessage } = await postCreateEvent({
				...payload,
			});

			if (errorMessage.value) {
				this.create.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.create.eventId = data.value.id;
			}
			this.isLoading = false;
		},

		async retrieveEventDetail(eventId: number) {
			// Don't clear `this.edit.data` so we can differentiate between first and next fetch data.
			this.isLoading = true;
			this.edit.errorMessage = '';
			const { data, errorMessage } = await getEventById(eventId);

			if (errorMessage.value) {
				this.edit.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.edit.data = data.value;
				this.edit.title = data.value.name;
				this.edit.surveyFormId = data.value.surveyForms?.id || null;
			}
			this.isLoading = false;
		},

		async updateEvent(
			eventId: number,
			payload: Partial<EventUpdateRequest>,
			isUpdateCoverImage = false,
			coverImageFile: File | Blob | null = null,
		) {
			this.isLoading = true;
			this.edit.errorMessage = '';
			this.edit.success = false;

			// Support save from publish event
			// If isUpdateCoverImage = true, it will either update or delete cover image before update event
			if (isUpdateCoverImage) {
				if (coverImageFile) {
					const { errorMessage } = await postEventUploadImageAPI(eventId, coverImageFile);

					if (errorMessage.value) {
						this.edit.errorMessage = errorMessage.value;
					}
				} else {
					const { errorMessage } = await deleteEventCoverImageAPI(eventId);
					if (errorMessage.value) {
						this.edit.errorMessage = errorMessage.value;
					}
				}
			}

			const { data, errorMessage } = await patchUpdateEvent(eventId, {
				...payload,
			});

			if (errorMessage.value) {
				this.edit.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.edit.success = true;
				this.edit.data = data.value;
				this.edit.title = data.value.name;
				this.edit.surveyFormId = data.value.surveyForms?.id || null;
			}
			this.isLoading = false;
		},

		async duplicateEvent(eventId: number, payload: EventDuplicateFormRequest) {
			this.duplicate.eventId = null;
			this.duplicate.errorMessage = '';
			this.duplicate.isLoading = true;

			const { data, errorMessage } = await postDuplicateEventByEventId(eventId, payload);

			if (errorMessage.value) {
				this.duplicate.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.duplicate.eventId = data.value.id;
			}
			this.duplicate.isLoading = false;
		},

		async duplicateEventSeries(eventId: number, payload: EventDuplicateFormRequest) {
			this.duplicate.eventId = null;
			this.duplicate.errorMessage = '';
			this.duplicate.isLoading = true;
			this.duplicate.eventSeriesInfo = null;

			const { errorMessage, data } = await postDuplicateEventSeriesByEventId(eventId, payload);

			if (errorMessage.value) {
				this.duplicate.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.duplicate.eventSeriesInfo = data.value;
			}

			this.duplicate.isLoading = false;
		},

		async getEventSeriesStatus(payload: EventSeriesPollingRequest) {
			this.duplicate.errorMessage = '';
			this.duplicate.isLoading = true;
			this.duplicate.eventSeriesResult = null;

			const { errorMessage, data } = await getEventSeriesStatus(payload);

			if (errorMessage.value) {
				this.duplicate.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.duplicate.eventSeriesResult = data.value;
			}

			this.duplicate.isLoading = false;
		},

		async deleteEvent(eventId: number) {
			this.isLoading = true;
			this.delete.errorMessage = '';

			const { errorMessage } = await postDeleteEventByEventId(eventId);

			if (errorMessage.value) {
				this.delete.errorMessage = errorMessage.value;
			}

			this.isLoading = false;
		},

		async temporarilyCancelEvent(eventId: number) {
			this.cancel.isLoading = true;
			this.cancel.errorMessage = '';

			const { data, errorMessage } = await postTemporarilyCancelEventByEventId(eventId);

			if (errorMessage.value) {
				this.cancel.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.cancel.data = data.value;
			}

			this.cancel.isLoading = false;
		},

		async confirmCancelEvent(eventId: number, eventCancellationId: number) {
			this.cancel.isLoading = true;
			this.cancel.errorMessage = '';

			const { errorMessage } = await postConfirmCancelEventByEventId(eventId, eventCancellationId);

			if (errorMessage.value) {
				this.cancel.errorMessage = errorMessage.value;
			}

			this.cancel.isLoading = false;
		},

		async discardCancelEvent(eventId: number) {
			this.cancel.isLoading = true;
			this.cancel.errorMessage = '';

			const { errorMessage } = await deleteCancelEventByEventId(eventId);

			if (errorMessage.value) {
				this.cancel.errorMessage = errorMessage.value;
			}

			this.cancel.isLoading = false;
		},

		async publishEvent(eventId: number, payload: Partial<EventUpdateRequest>) {
			this.isLoading = true;
			this.edit.errorMessage = '';
			this.edit.success = false;
			this.edit.isPublishing = true;

			const { data, errorMessage } = await postPublishEvent(eventId, {
				...payload,
			});

			if (errorMessage.value) {
				this.edit.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.edit.success = true;
				this.edit.data = data.value;
				this.edit.title = data.value.name;
				this.edit.surveyFormId = data.value.surveyForms?.id || null;
			}
			this.isLoading = false;
			this.edit.isPublishing = false;
		},

		async attachEventForm(eventId: number, newFormId: number, previousFormId?: number) {
			this.isLoading = true;
			this.edit.errorMessage = '';

			if (previousFormId) {
				const { errorMessage } = await postDetachEventFormByEventId(eventId, previousFormId);

				if (errorMessage.value) {
					this.edit.errorMessage = errorMessage.value;
					this.isLoading = false;
					return;
				}
			}

			const { errorMessage } = await postAttachEventFormByEventId(eventId, newFormId);

			if (errorMessage.value) {
				this.edit.errorMessage = errorMessage.value;
			}

			this.isLoading = false;
		},

		async detachEventForm(eventId: number, formId: number) {
			this.edit.errorMessage = '';
			this.isLoading = true;

			const { errorMessage } = await postDetachEventFormByEventId(eventId, formId);

			if (errorMessage.value) {
				this.edit.errorMessage = errorMessage.value;
			}

			this.isLoading = false;
		},

		async uploadEventCoverImage(eventId: number, file: File | Blob) {
			this.isLoading = true;
			this.edit.errorMessage = '';
			this.edit.isUpdatedCoverImage = false;

			const { errorMessage } = await postEventUploadImageAPI(eventId, file);

			if (errorMessage.value) {
				this.edit.errorMessage = errorMessage.value;
			} else {
				this.edit.isUpdatedCoverImage = true;
			}

			this.isLoading = false;
		},

		async deleteEventCoverImage(eventId: number) {
			this.isLoading = true;
			this.edit.errorMessage = '';
			this.edit.isUpdatedCoverImage = false;

			const { errorMessage } = await deleteEventCoverImageAPI(eventId);

			if (errorMessage.value) {
				this.edit.errorMessage = errorMessage.value;
			} else {
				this.edit.isUpdatedCoverImage = true;
			}

			this.isLoading = false;
		},

		async generateCoverImageByAI(eventId: number) {
			this.aiCoverImage.isLoading = true;
			this.aiCoverImage.errorMessage = '';
			this.aiCoverImage.url = '';

			const { data, errorMessage } = await postGenerateCoverImageByAI(eventId);

			if (errorMessage.value) {
				this.aiCoverImage.errorMessage = errorMessage.value;
			}

			if (data.value) {
				this.aiCoverImage.url = data.value.imageUrl;
			}

			this.aiCoverImage.isLoading = false;
		},

		async sendMessageToOrganizer(
			eventId: number,
			info: MessageInfo,
		) {
			this.isLoading = true;
			this.message.errorMessage = '';

			const { errorMessage } = await postMessageToOrganizer(
				eventId,
				info,
			);

			if (errorMessage.value) {
				this.message.errorMessage = errorMessage.value;
			}

			this.isLoading = false;
		},
	},
});

export default useEventStore;
